import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getItem, deleteItem } from '../../lib/api';
import type { InventoryItem } from '../../types';
import QRCodeDisplay from './QRCodeDisplay';

interface Props {
  itemId: number;
  onEdit: (item: InventoryItem) => void;
  onIssue: (item: InventoryItem) => void;
  onReceive: (item: InventoryItem) => void;
  onDeleted: () => void;
}

export default function InventoryDetail({ itemId, onEdit, onIssue, onReceive, onDeleted }: Props) {
  const qc = useQueryClient();
  const { data: item, isLoading } = useQuery({
    queryKey: ['item', itemId],
    queryFn: () => getItem(itemId),
  });

  const del = useMutation({
    mutationFn: () => deleteItem(itemId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory'] });
      onDeleted();
    },
  });

  if (isLoading) {
    return <div className="flex items-center justify-center h-full text-sm text-gray-500">Loading...</div>;
  }
  if (!item) return null;

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="p-6 flex-1">
        {/* Header row */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900 uppercase leading-tight">{item.name}</h2>
            {item.item_type_name && (
              <p className="text-sm text-brand-600 mt-0.5">{item.item_type_name}</p>
            )}
          </div>
          {/* QR Code */}
          <div className="shrink-0">
            <QRCodeDisplay value={item.part_number} size={110} />
          </div>
        </div>

        {/* Qty */}
        <div className="mb-5">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">In Stock</p>
          <p className={`text-4xl font-bold ${item.qty <= 2 ? 'text-red-600' : 'text-gray-900'}`}>
            {item.qty}
          </p>
          <p className="text-sm text-gray-500 mt-0.5">{item.unit_of_measure}</p>
        </div>

        {/* Description */}
        {item.part_description && (
          <p className="text-sm text-gray-700 mb-4 leading-relaxed"
             dangerouslySetInnerHTML={{ __html: item.part_description }} />
        )}

        {/* Location */}
        {item.warehouse_name && (
          <p className="text-sm text-gray-600 mb-2">
            <span className="font-medium">Located in </span>{item.warehouse_name}
          </p>
        )}

        {/* Notes */}
        {item.notes && (
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700">Additional Notes:</p>
            <p className="text-sm text-gray-600 mt-1">{item.notes}</p>
          </div>
        )}

        {/* Metadata sidebar */}
        <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-2 text-sm border-t border-gray-100 pt-4">
          <Meta label="Part Number"  value={item.part_number} />
          {item.model_name && <Meta label="Model Name" value={item.model_name} />}
          {item.warehouse_name && <Meta label="Warehouse"   value={item.warehouse_name} />}
          {item.manufacturer_name && <Meta label="Manufacturer" value={item.manufacturer_name} />}
          {item.vendor_name && <Meta label="Vendor"     value={item.vendor_name} />}
          <Meta label="Item Cost"   value={`$${item.cost_of_item.toFixed(2)}`} />
        </div>
      </div>

      {/* Action Bar */}
      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => onIssue(item)}>Issue Part</button>
          <button className="btn-secondary" onClick={() => onReceive(item)}>Receive Part</button>
          <button
            className="text-sm font-medium text-red-600 hover:text-red-700 px-2"
            onClick={() => {
              if (confirm(`Delete "${item.name}"? This cannot be undone.`)) del.mutate();
            }}
          >
            Delete Part
          </button>
        </div>
        <button className="btn-secondary" onClick={() => onEdit(item)}>Edit Part</button>
      </div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="font-medium text-gray-700">{label}: </span>
      <span className="text-gray-600">{value}</span>
    </div>
  );
}
